import { Component, DestroyRef, Inject, NgZone, OnDestroy, OnInit, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Params, QueryParamsHandling, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { datadogRum } from '@datadog/browser-rum';
import { Subscription, finalize } from 'rxjs';
import { SeAnalyticsService } from 'se-analytics';
import { SeFeConfirmService } from 'se-fe-confirm';
import { SeFePaginationChange, SeFePaginationOptions } from 'se-fe-pagination';
import { InitalSort as InitialSort, SortChangeEvent } from 'se-fe-table';
import { SeFeToastService, SeFeToastTypes } from 'se-fe-toast';
import { LoadScriptsService } from 'src/app/services/load-scripts.service';
import { CURRENT_ORG_ID } from '../../providers/current-org-id.provider';
import { Item } from '../models/item.model';
import { PagedParameters } from '../pagination/pagedParameters';
import { PagingService } from '../pagination/paging.service';
import { ItemService } from '../services/item.service';
import { StoreStatusService } from '../services/store-status.service';
import { ListViewItem } from '../models/list-view-item.model';
import { RegistrationWarningModalComponent } from '../registration-warning-modal/registration-warning-modal.component';
import { SurveyService } from '../services/survey.service';

type SortDirection = 'asc' | 'desc';
type ItemListTab = 'active' | 'archived';
const baseDisplayedColumns = ['created_at', 'name', 'price_variations_count', 'price', 'sold_count'];

@Component({
  selector: 'app-item-list',
  standalone: false,
  providers: [PagingService],
  styleUrls: ['./item-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './item-list.component.html',
})
export class ItemListComponent implements OnInit, OnDestroy {
  private readonly registrationWarningModal = viewChild.required(RegistrationWarningModalComponent);

  public activeTab: ItemListTab = 'active';
  public archivedItemActionId: number | null = null;
  public archivingItemId: number | null = null;
  public readonly bulkArchiveLimit = 100;
  public bulkArchiveLimitExceeded = false;
  public bulkArchiving = false;
  public currentOrgId: string;
  public displayedColumns: string[] = ['select', ...baseDisplayedColumns];
  public hasSku = false;
  public items: ListViewItem[] = [];
  public loading = true;
  public paginationOptions: SeFePaginationOptions = {
    label: 'Sale Items',
    name: 'saleItemsPagination',
    currentPage: 1,
    limit: 25,
    total: 0,
  };
  public searchDirtied = false;
  public searchTerm = '';
  public selectedItemIds = new Set<string | number>();
  public readonly searchControl = new FormControl('', { nonNullable: true });
  public readonly searchForm = new FormGroup({ search: this.searchControl });
  public readonly searchFieldConfig = {
    label: 'Search',
    hiddenLabel: true,
  };
  public showItemModal = false;
  public showOverlay = false;
  public showTaxCode = false;
  public sortColumn = 'created_at';
  public sortDirection: SortDirection = 'desc';
  public sortState: InitialSort = { column: this.sortColumn, direction: this.sortDirection };

  private readonly sortableColumns = ['created_at', 'name', 'price_variations_count', 'sold_count'];
  private queryParamSubscription: Subscription;
  private routeParamsInitialized = false;
  private storeStatusSubscription: Subscription;

  constructor(
    @Inject(CURRENT_ORG_ID) currentOrgId: string,
    private destroyRef: DestroyRef,
    private itemService: ItemService,
    private pagingService: PagingService,
    private surveyService: SurveyService,
    private storeStatusService: StoreStatusService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
    private confirmService: SeFeConfirmService,
    private toastService: SeFeToastService,
    private translateService: TranslateService,
    private seAnalyticsService: SeAnalyticsService,
    private loadScriptsService: LoadScriptsService
  ) {
    this.currentOrgId = currentOrgId;
  }

  search(searchTerm: string): void {
    this.searchTerm = searchTerm.trim();
    this.searchControl.setValue(this.searchTerm, { emitEvent: false });
    if (this.searchTerm.length === 0) {
      this.searchDirtied = false;
      this.navigate({ search_term: '', page: 1 });
      this.loadItems();
      return;
    }
    this.searchDirtied = true;
    this.navigate({ search_term: this.searchTerm, page: 1 });
  }

  submitSearch(): void {
    this.search(this.searchControl.value);
  }

  loadItems(): void {
    this.showOverlay = true;

    this.itemService.getItems(this.combinedApiParams()).subscribe((items) => {
      this.items = items.result.map((item) => new ListViewItem(item));
      this.collectPagination(items.metadata?.pagination);
      this.hasSku = this.checkForSku();
      this.setDisplayedColumns();
      this.loading = false;
      this.showOverlay = false;
    });
  }

  navigate(params: Params, strategy: QueryParamsHandling = 'merge'): void {
    this.ngZone.run(() => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: params,
        queryParamsHandling: strategy,
      });
    });
  }

  ngOnInit(): void {
    this.loadScriptsService
      .loadScripts('saleItem')
      .catch((error) => datadogRum.addError('Unable to load sale item modal', { error }));
    this.setPaginationLabel();
    this.subscribeToRouteParams();
    this.subscribeToStoreStatus();
    if (!this.storeStatusService.isSet) {
      this.storeStatusService.setStoreStatus(this.currentOrgId);
    }
    this.seAnalyticsService.trackEvent('pageView', {
      depth1: 'HQ',
      depth2: 'SaleItems',
    });
    datadogRum.addAction('Item List', {
      currentOrgId: this.currentOrgId,
      items: this.items,
    });
  }

  private subscribeToStoreStatus(): void {
    this.storeStatusSubscription = this.storeStatusService.$storeStatus.subscribe((storeStatus) => {
      this.showTaxCode = storeStatus.taxable;
    });
  }

  combinedApiParams(): Params {
    const params: Params = {
      organization_id: this.currentOrgId,
      exclude_fees: true,
      exclude_products: true,
      membership: false,
      search_fields: 'items.name,item_variations.sku',
      search_term: this.searchTerm.trim(),
      list: 'list_view'
    };

    if (this.activeTab === 'archived') {
      params.only_disabled = true;
    }

    params[this.sortColumnApiName(this.sortColumn)] = this.sortDirection;
    return this.pagingService.toApiParams(params);
  }

  private sortColumnApiName(colname: string): string {
    return 'order[' + colname + ']';
  }

  subscribeToRouteParams(): void {
    this.queryParamSubscription = this.activatedRoute.queryParamMap.subscribe((params) => {
      if (this.routeParamsInitialized && this.registrationWarningModal().isOpen) {
        this.registrationWarningModal().close();
      }
      this.routeParamsInitialized = true;

      this.activeTab = params.get('tab') === 'archived' ? 'archived' : 'active';
      const searchTerm = params.get('search_term');
      this.searchTerm = searchTerm || '';
      this.searchControl.setValue(this.searchTerm, { emitEvent: false });
      this.setSortFromRouteParams(params);
      this.loadItems();
    });
  }

  sortColumns(sortChange: SortChangeEvent | string): void {
    if (typeof sortChange !== 'string' && sortChange.initial) {
      return;
    }

    const columnKey = typeof sortChange === 'string' ? sortChange : sortChange.column;
    const sortDirection =
      typeof sortChange === 'string' ? this.nextSortDirection(columnKey) : this.normalizeSortDirection(sortChange);

    this.setSortState(columnKey, sortDirection);

    const sortCol = this.sortColumnApiName(this.sortColumn);
    const params: Params = {
      page: 1,
      per_page: this.pagingService.perPage,
      search_term: this.searchTerm,
    };
    if (this.activeTab === 'archived') {
      params.tab = 'archived';
    }
    params[sortCol] = this.sortDirection;
    this.navigate(params, '');
  }

  moreThanOneVariation(item: ListViewItem): boolean {
    return item.price_variations_count > 0;
  }

  checkForSku(): boolean {
    return this.items.some((item: ListViewItem) => !!item.sku);
  }

  listIsEmpty(): boolean {
    return this.activeTab === 'active' && !this.items.length && !this.searchTerm && !this.searchDirtied;
  }

  searchedListIsEmpty(): boolean {
    return !this.items.length && (!!this.searchTerm || this.searchDirtied);
  }

  archivedListIsEmpty(): boolean {
    return this.activeTab === 'archived' && !this.items.length && !this.searchTerm && !this.searchDirtied;
  }

  listIsLoading(): boolean {
    return this.showOverlay || this.bulkArchiving;
  }

  isItemSelectionDisabled(itemId: string | number): boolean {
    return (
      this.bulkArchiving ||
      (this.selectedItemIds.size >= this.bulkArchiveLimit && !this.selectedItemIds.has(itemId))
    );
  }

  isSelectAllItemsDisabled(): boolean {
    return (
      this.bulkArchiving ||
      !this.items.length ||
      (this.selectedItemIds.size >= this.bulkArchiveLimit && !this.allVisibleItemsSelected())
    );
  }

  toggleItemSelection(itemId: string | number, selected: boolean): void {
    if (this.activeTab !== 'active' || this.bulkArchiving) {
      return;
    }

    if (!selected) {
      this.selectedItemIds.delete(itemId);
      this.bulkArchiveLimitExceeded = false;
      return;
    }

    if (!this.visibleItemIds().includes(itemId)) {
      return;
    }

    if (this.selectedItemIds.size >= this.bulkArchiveLimit) {
      this.bulkArchiveLimitExceeded = true;
      return;
    }

    this.selectedItemIds.add(itemId);
    this.bulkArchiveLimitExceeded = false;
  }

  toggleAllVisibleItems(selected: boolean): void {
    if (this.activeTab !== 'active' || this.bulkArchiving) {
      return;
    }

    const visibleItemIds = this.visibleItemIds();
    if (!selected) {
      visibleItemIds.forEach((itemId) => this.selectedItemIds.delete(itemId));
      this.bulkArchiveLimitExceeded = false;
      return;
    }

    const unselectedVisibleItemIds = visibleItemIds.filter((itemId) => !this.selectedItemIds.has(itemId));
    const availableSelectionCount = Math.max(this.bulkArchiveLimit - this.selectedItemIds.size, 0);

    unselectedVisibleItemIds
      .slice(0, availableSelectionCount)
      .forEach((itemId) => this.selectedItemIds.add(itemId));
    this.bulkArchiveLimitExceeded = unselectedVisibleItemIds.length > availableSelectionCount;
  }

  allVisibleItemsSelected(): boolean {
    const visibleItemIds = this.visibleItemIds();
    return (
      this.activeTab === 'active' &&
      !!visibleItemIds.length &&
      visibleItemIds.every((itemId) => this.selectedItemIds.has(itemId))
    );
  }

  someVisibleItemsSelected(): boolean {
    const visibleItemIds = this.visibleItemIds();
    return (
      !this.allVisibleItemsSelected() && visibleItemIds.some((itemId) => this.selectedItemIds.has(itemId))
    );
  }

  clearSelection(): void {
    this.selectedItemIds.clear();
    this.bulkArchiveLimitExceeded = false;
  }

  confirmBulkArchive(event: MouseEvent): void {
    if (this.activeTab !== 'active' || !this.selectedItemIds.size || this.bulkArchiving) {
      return;
    }

    this.confirmService.confirm({
      title: this.translateService.instant('LIST_VIEW.BULK_ARCHIVE_MODAL.title'),
      message: this.translateService.instant('LIST_VIEW.BULK_ARCHIVE_MODAL.message'),
      actions: [
        {
          label: this.translateService.instant('LIST_VIEW.BULK_ARCHIVE_MODAL.cancel'),
          action: () => null,
        },
        {
          label: this.translateService.instant('LIST_VIEW.BULK_ARCHIVE_MODAL.confirm_action'),
          primary: true,
          action: () => this.bulkArchive(),
        },
      ],
      triggerEl: event.currentTarget as HTMLElement,
    });
  }

  bulkArchive(): void {
    if (this.activeTab !== 'active' || !this.selectedItemIds.size || this.bulkArchiving) {
      return;
    }

    const selectedItemIds = Array.from(this.selectedItemIds);
    this.bulkArchiving = true;

    this.itemService
      .bulkArchiveItems(selectedItemIds)
      .pipe(finalize(() => (this.bulkArchiving = false)))
      .subscribe({
        next: () => {
          this.clearSelection();
          this.toastService.addToast({
            type: SeFeToastTypes.Success,
            message: this.translateService.instant('LIST_VIEW.BULK_ARCHIVE_MODAL.success', {
              count: selectedItemIds.length,
            }),
          });
          this.loadItems();
        },
        error: (error) => {
          this.toastService.addToast({
            type: SeFeToastTypes.Error,
            message:
              error?.error?.error?.messages?.[0] ||
              error?.error?.messages?.[0] ||
              this.translateService.instant('LIST_VIEW.BULK_ARCHIVE_MODAL.ERROR.default'),
          });
        },
      });
  }

  confirmArchive(event: MouseEvent, item: ListViewItem): void {
    if (this.activeTab !== 'active' || this.archivingItemId) {
      return;
    }

    this.confirmService.confirm({
      title: this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.title', { name: item.name }),
      message: this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.message'),
      actions: [
        {
          label: this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.cancel'),
          action: () => null,
        },
        {
          label: this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.confirm_action'),
          primary: true,
          action: () => this.archiveItem(item),
        },
      ],
      triggerEl: event.currentTarget as HTMLElement,
    });
  }

  archiveItem(item: ListViewItem): void {
    if (this.activeTab !== 'active' || this.archivingItemId) {
      return;
    }

    this.archivingItemId = item.id;
    this.itemService.deleteItem(item.id).subscribe({
      next: () => {
        this.archivingItemId = null;
        this.toastService.addToast({
          type: SeFeToastTypes.Success,
          message: this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.success', { name: item.name }),
        });
        this.loadItems();
      },
      error: (error) => {
        this.archivingItemId = null;
        this.toastService.addToast({
          type: SeFeToastTypes.Error,
          message:
            error?.error?.error?.messages?.[0] ||
            error?.error?.messages?.[0] ||
            this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.ERROR.default', { name: item.name }),
        });
      },
    });
  }

  unarchiveItem(item: ListViewItem): void {
    if (this.activeTab !== 'archived' || this.archivedItemActionId) {
      return;
    }

    this.archivedItemActionId = item.id;
    this.itemService.unarchiveItem(item.id).subscribe({
      next: () => {
        this.archivedItemActionId = null;
        this.toastService.addToast({
          type: SeFeToastTypes.Success,
          message: this.translateService.instant('LIST_VIEW.UNARCHIVE.success', { name: item.name }),
        });
        this.loadItems();
      },
      error: (error) => {
        this.archivedItemActionId = null;
        this.toastService.addToast({
          type: SeFeToastTypes.Error,
          message:
            error?.error?.error?.messages?.[0] ||
            error?.error?.messages?.[0] ||
            this.translateService.instant('LIST_VIEW.UNARCHIVE.ERROR.default', { name: item.name }),
        });
      },
    });
  }

  confirmPermanentDelete(event: MouseEvent, item: ListViewItem): void {
    if (this.activeTab !== 'archived' || !item.can_delete || this.archivedItemActionId) {
      return;
    }

    const triggerEl = event.currentTarget as HTMLElement;
    this.archivedItemActionId = item.id;

    this.surveyService
      .getBySaleItemUuids(item.uuids, this.currentOrgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (response) => {
        const lookupIsCurrent = this.registrationLookupIsCurrent(item);
        this.archivedItemActionId = null;

        if (!lookupIsCurrent) {
          return;
        }

        if (response.result.length) {
          this.registrationWarningModal().open(response.result, triggerEl);
          return;
        }

        this.openPermanentDeleteConfirmation(triggerEl, item);
      },
      error: () => {
        const lookupIsCurrent = this.registrationLookupIsCurrent(item);
        this.archivedItemActionId = null;

        if (!lookupIsCurrent) {
          return;
        }

        this.toastService.addToast({
          type: SeFeToastTypes.Error,
          message: this.translateService.instant('LIST_VIEW.REGISTRATION_WARNING_MODAL.ERROR.lookup', {
            name: item.name,
          }),
        });
      },
    });
  }

  permanentlyDeleteItem(item: ListViewItem): void {
    if (this.activeTab !== 'archived' || !item.can_delete || this.archivedItemActionId) {
      return;
    }

    this.archivedItemActionId = item.id;
    this.itemService.deleteItem(item.id).subscribe({
      next: () => {
        this.archivedItemActionId = null;
        this.toastService.addToast({
          type: SeFeToastTypes.Success,
          message: this.translateService.instant('LIST_VIEW.PERMANENT_DELETE_MODAL.success', { name: item.name }),
        });
        this.loadItems();
      },
      error: (error) => {
        this.archivedItemActionId = null;
        this.toastService.addToast({
          type: SeFeToastTypes.Error,
          message:
            error?.error?.error?.messages?.[0] ||
            error?.error?.messages?.[0] ||
            this.translateService.instant('LIST_VIEW.PERMANENT_DELETE_MODAL.ERROR.default', { name: item.name }),
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.pagingService.unsubscribe();
    this.queryParamSubscription.unsubscribe();
    this.storeStatusSubscription.unsubscribe();
  }

  onSuccess(item: Item): void {
    this.loadItems();
    this.toastService.addToast({
      type: SeFeToastTypes.Success,
      message: `"${item.name}" sale item added`,
    });
  }

  variationsDisplayCount(item: Item): number | string {
    const variations_count = item.variations.length;
    return variations_count > 1 ? variations_count : '--';
  }

  public toggleItemModal(): void {
    this.showItemModal = !this.showItemModal;
  }

  public selectTab(tab: ItemListTab, event?: Event): void {
    event?.preventDefault();

    if (tab === this.activeTab) {
      return;
    }

    this.activeTab = tab;
    this.clearSelection();
    this.setDisplayedColumns();
    this.searchDirtied = false;
    this.navigate({
      page: 1,
      search_term: '',
      tab: tab === 'archived' ? 'archived' : null,
    });
  }

  public handlePagination(paginationData: SeFePaginationChange): void {
    const { options, queryParams } = this.pagingService.applyPaginationChange(paginationData, this.paginationOptions);
    this.paginationOptions = options;
    this.navigate(queryParams);
  }

  private collectPagination(pagination: PagedParameters | undefined): void {
    this.paginationOptions = this.pagingService.syncPaginationFromResponse(pagination, this.paginationOptions);
  }

  private openPermanentDeleteConfirmation(triggerEl: HTMLElement, item: ListViewItem): void {
    this.confirmService.confirm({
      title: this.translateService.instant('LIST_VIEW.PERMANENT_DELETE_MODAL.title', { name: item.name }),
      message: this.translateService.instant('LIST_VIEW.PERMANENT_DELETE_MODAL.message'),
      actions: [
        {
          label: this.translateService.instant('LIST_VIEW.PERMANENT_DELETE_MODAL.cancel'),
          action: () => null,
        },
        {
          label: this.translateService.instant('LIST_VIEW.PERMANENT_DELETE_MODAL.confirm_action'),
          destructive: true,
          action: () => this.permanentlyDeleteItem(item),
        },
      ],
      triggerEl,
    });
  }

  private registrationLookupIsCurrent(item: ListViewItem): boolean {
    const currentItem = this.items.find(({ id }) => id === item.id);

    return this.activeTab === 'archived' && currentItem?.can_delete === true && this.archivedItemActionId === item.id;
  }

  private nextSortDirection(columnKey: string): SortDirection {
    return columnKey !== this.sortColumn || this.sortDirection === 'desc' ? 'asc' : 'desc';
  }

  private setDisplayedColumns(): void {
    const columns = this.activeTab === 'active' ? ['select', ...baseDisplayedColumns] : [...baseDisplayedColumns];

    if (this.hasSku) {
      columns.push('sku');
    }

    if (this.activeTab === 'active') {
      columns.push('archive');
    } else {
      columns.push('archived_actions');
    }

    this.displayedColumns = columns;
  }

  private visibleItemIds(): Array<string | number> {
    return this.items
      .filter((item) => Number.isInteger(item.id) && item.id > 0)
      .map((item) => item.id);
  }

  private setPaginationLabel(): void {
    this.paginationOptions = {
      ...this.paginationOptions,
      label: this.translateService.instant('LIST_VIEW.title') || 'Sale Items',
    };
  }

  private setSortFromRouteParams(params: ParamMap): void {
    const sortedColumn = this.sortableColumns.find((column) => params.has(this.sortColumnApiName(column)));
    const sortColumn = sortedColumn || 'created_at';
    const sortDirection = sortedColumn
      ? params.get(this.sortColumnApiName(sortColumn)) === 'desc'
        ? 'desc'
        : 'asc'
      : 'desc';

    this.setSortState(sortColumn, sortDirection);
  }

  private normalizeSortDirection(sortChange: SortChangeEvent): SortDirection {
    return sortChange.direction === 'desc' ? 'desc' : 'asc';
  }

  private setSortState(column: string, direction: SortDirection): void {
    this.sortColumn = column;
    this.sortDirection = direction;
    this.sortState = { column, direction };
  }
}
